// 場所（ピックアップ先・ドロップ先）の追加・直す。場所の管理と、配送指示の画面（急なスポットの依頼の新しい場所）で使う。
// D1 には緯度経度（暗号化）と目安だけ、名前・住所・電話・メモは Firestore に書く
import { useState, type FormEvent } from 'react';
import { PlaceCreate, type Place } from '@torapon/shared';
import { api, HttpError } from '../lib/api';
import { useDirectory } from '../lib/directory';
import { putPlace } from '../lib/fs';
import { geocode } from '../lib/geocode';
import { useMe } from '../lib/me';
import { Dialog, useToast } from './ui';

export function PlaceDialog({ place, title, favorite = true, submitLabel, onClose, onDone }: {
  place: Place | null; title?: string; favorite?: boolean; submitLabel?: string;
  onClose: () => void; onDone: (id: string) => void;
}) {
  const toast = useToast();
  const me = useMe();
  const dir = useDirectory();
  const doc = place ? dir.places.get(place.id) : undefined;
  const [f, setF] = useState({
    name: doc?.name ?? '', address: doc?.address ?? '', phone: doc?.phone ?? '', memo: doc?.memo ?? '',
    lat: place ? String(place.lat) : '', lng: place ? String(place.lng) : '', loadMin: String(place?.loadMin ?? 10), unloadMin: String(place?.unloadMin ?? 10), isFavorite: place?.isFavorite ?? favorite,
  });
  // 位置を出した住所（住所を直したら出し直す）
  const [found, setFound] = useState<{ address: string; label: string } | null>(place ? { address: doc?.address ?? '', label: '' } : null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [manual, setManual] = useState(false);
  const locate = async (address = f.address) => {
    setErr('');
    if (!address.trim()) return null;
    const g = await geocode(address).catch(() => null);
    if (!g) { setErr('住所から位置を見つけられませんでした。住所を確かめるか、「緯度・経度を自分で入れる」から入れてください'); setFound(null); return null; }
    setF((x) => ({ ...x, lat: g.lat.toFixed(6), lng: g.lng.toFixed(6) }));
    setFound({ address, label: g.label });
    return g;
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr('');
    setBusy(true);
    try {
      // 住所を入れたまま位置を出していなければ、ここで出す
      let { lat, lng } = f;
      if (!manual && (!lat || !lng || found?.address !== f.address)) {
        const g = await locate();
        if (!g) return;
        lat = String(g.lat); lng = String(g.lng);
      }
      const r = PlaceCreate.safeParse({ name: f.name, address: f.address, phone: f.phone || undefined, memo: f.memo || undefined, lat: Number(lat), lng: Number(lng), isFavorite: f.isFavorite, loadMin: Number(f.loadMin), unloadMin: Number(f.unloadMin) });
      if (!r.success || !lat || !lng) { setErr(!lat || !lng ? '位置（緯度・経度）を入れてください' : r.error!.issues[0]?.message ?? '入力を確かめてください'); return; }
      const { name, address, phone, memo, ...reg } = r.data;
      const id = place ? place.id : (await api.post<{ id: string }>('/v1/office/places', reg)).id;
      if (place) await api.patch(`/v1/office/places/${place.id}`, reg, { ifMatch: place.version });
      await putPlace(id, { officeId: me!.officeId, name, address, phone: phone ?? null, memo: memo ?? null });
      toast(place ? '場所を直しました' : '場所を追加しました');
      onDone(id);
    } catch (er) { setErr(er instanceof HttpError ? er.message : '保存できませんでした'); }
    finally { setBusy(false); }
  };
  const num = (k: 'lat' | 'lng' | 'loadMin' | 'unloadMin') => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog title={title ?? (place ? '場所を直す' : '場所を追加')} onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
        <div className="field"><label htmlFor="pn">名前（必須・40 文字まで）</label><input id="pn" maxLength={40} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="例：○○商店 本店" autoFocus /></div>
        <div className="field"><label htmlFor="pa">住所（必須）</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input id="pa" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} onBlur={() => { if (!manual && f.address.trim() && found?.address !== f.address) void locate(); }} placeholder="例：札幌市中央区北1条西2丁目" />
            <button type="button" className="btn sm" onClick={() => void locate()}>位置を出す</button>
          </div>
          {found && found.label && found.address === f.address && <small className="muted" aria-live="polite">位置：{found.label}（{f.lat}, {f.lng}）</small>}
        </div>
        {manual ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div className="field"><label htmlFor="plat">緯度</label><input id="plat" inputMode="decimal" value={f.lat} onChange={num('lat')} placeholder="43.0621" /></div>
            <div className="field"><label htmlFor="plng">経度</label><input id="plng" inputMode="decimal" value={f.lng} onChange={num('lng')} placeholder="141.3544" /></div>
          </div>
        ) : <button type="button" className="btn sm" style={{ justifySelf: 'start' }} onClick={() => setManual(true)}>緯度・経度を自分で入れる</button>}
        <div className="field"><label htmlFor="pp">電話</label><input id="pp" maxLength={20} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
        <div className="field"><label htmlFor="pm">メモ（200 文字まで）</label><input id="pm" maxLength={200} value={f.memo} onChange={(e) => setF({ ...f, memo: e.target.value })} placeholder="例：裏口から入る" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div className="field"><label htmlFor="pl">荷積みの目安（分）</label><input id="pl" inputMode="numeric" value={f.loadMin} onChange={num('loadMin')} /></div>
          <div className="field"><label htmlFor="pu">荷下ろしの目安（分）</label><input id="pu" inputMode="numeric" value={f.unloadMin} onChange={num('unloadMin')} /></div>
        </div>
        <label><input type="checkbox" style={{ width: 'auto' }} checked={f.isFavorite} onChange={(e) => setF({ ...f, isFavorite: e.target.checked })} /> お気に入り（配送指示の右にいつも出す）</label>
        {err && <p className="err" role="alert">{err}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button type="button" className="btn" onClick={onClose}>やめる</button><button className="btn pri" disabled={busy}>{submitLabel ?? (place ? '直す' : '追加する')}</button></div>
      </form>
    </Dialog>
  );
}
